import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'
import type { MallCategory, MallDetailBlock, MallProduct, MallQna, MallReview } from '@/constants/mallProducts'
import type { MallImageShows, MallSellUnit, MallShippingCharge } from '@/utils/mallSell'
import {
  normalizeImageShows,
  normalizePackQty,
  normalizeSellUnit,
  normalizeShippingCharge,
  normalizeOrderStep,
  normalizeShippingEvery,
} from '@/utils/mallSell'

/**
 * 함우리몰 상품 API.
 * 판매업체가 등록·수정하는 쪽과, 구매자에게 열려 있는 조회 쪽을 한 파일에 둔다.
 */
export type MallProductStatus =
  | 'DRAFT'
  | 'REQUESTED'
  | 'SELLING'
  | 'SOLDOUT'
  | 'HIDDEN'
  | 'REJECTED'

export const MALL_PRODUCT_STATUS_LABEL: Record<MallProductStatus, string> = {
  DRAFT: '임시저장',
  REQUESTED: '검수요청',
  SELLING: '판매중',
  SOLDOUT: '품절',
  HIDDEN: '숨김',
  REJECTED: '반려',
}

/** 함우리몰 구매자 화면에 실제로 보이는 상태(판매중·품절) */
export const MALL_LIVE_STATUSES: MallProductStatus[] = ['SELLING', 'SOLDOUT']

export function isMallLiveStatus(status: MallProductStatus | string | null | undefined): boolean {
  return MALL_LIVE_STATUSES.includes(String(status || '') as MallProductStatus)
}

/** 목록용 몰 노출 한 줄. 판매상태와 별도로 읽히게 한다. */
export function mallLiveLabel(status: MallProductStatus | string | null | undefined): string {
  if (status === 'SELLING') return '몰 노출중'
  if (status === 'SOLDOUT') return '몰 노출중 · 품절'
  return '몰 비노출'
}

export interface MallProductSpecInput {
  label: string
  value: string
}

export interface MallProductOptionInput {
  group: string
  name: string
  extra: number
  stock: number
  use: boolean
  front?: boolean
}

/** 검수 이력 한 줄 */
export interface MallProductLog {
  action: 'REQUEST' | 'APPROVE' | 'REJECT' | 'EDIT' | 'HIDE'
  actor: string
  role: 'SELLER' | 'ADMIN'
  memo: string
  at: string
}

export interface MallProductBlockInput {
  type: MallDetailBlock['type']
  text: string
  imageUrl: string
  alt: string
}

export interface MallProductPayload {
  name: string
  category: MallCategory
  summary: string
  description: string
  unit: string
  thumbnail: string
  images: string[]
  listPrice: number
  price: number
  /** 원가 — 판매업체 공급가 */
  cost: number
  /** 총 마진 중 함우리 몫(%) */
  marginShare: number
  stock: number
  minOrder: number
  orderStep?: number
  maxOrder: number
  sellUnit?: MallSellUnit
  imageShows?: MallImageShows
  packQty?: number
  shippingCharge?: MallShippingCharge
  shippingEvery?: number
  shippingFee: number
  freeShipOver: number
  leadTime: string
  origin: string
  maker: string
  badge: string
  status: MallProductStatus
  /** 판매업체가 검수자에게 남기는 메모 */
  requestNote: string
  /** REGULAR 상시몰 · SALE 공동구매몰. 없으면 공동구매몰 */
  mallKind?: 'REGULAR' | 'SALE'
  /** 공동구매 특별할인 코드. 상시몰은 비운다 */
  saleCode?: string
  /** 환불 배송비 기준 수량 */
  returnEvery?: number
  /** 환불 배송비. 기준 수량마다 받는 금액 */
  returnFee?: number
  /** 옵션 가격에 나눌 배송비. 고객 청구 배송비가 아니다 */
  allocShipFee?: number
  /** 배송비 1회로 보내는 최대 수량 */
  allocShipMax?: number
  /** 공급가·판매가의 기준 수량 */
  basisQty?: number
  /** 기준 수량 공급가. 배송비 제외 */
  basisCost?: number
  /** 기준 수량 판매가격. 배송비 제외 */
  basisPrice?: number
  blocks: MallProductBlockInput[]
  specs: MallProductSpecInput[]
  options: MallProductOptionInput[]
  tags: string[]
  /** 관리자 내부 메모 — admin API에만 실림 */
  adminMemo?: string
}

/**
 * 서버가 돌려주는 상품 한 건.
 * 목록 응답은 가벼워야 해서 images · blocks · specs · options · reviews 를 싣지 않는다.
 * 대신 몇 장 · 몇 개인지는 imageCount · optionCount 로 온다.
 */
export interface ServerMallProduct
  extends Omit<MallProductPayload, 'images' | 'blocks' | 'specs' | 'options' | 'cost' | 'marginShare'> {
  code: string
  /* 원가 · 마진 배분율은 우리 쪽 정보라 구매자용 응답에는 실리지 않는다 */
  cost?: number
  marginShare?: number
  sellerCode?: string
  storeName: string
  /** 판매업체 당일 출고 마감 (예: 14:00) */
  storeCutoff?: string
  /** 판매업체 교환·반품 안내 */
  storeReturnPolicy?: string
  /** 판매업체 반품 배송비 */
  storeReturnFee?: number
  images?: string[]
  imageCount: number
  optionCount: number
  /** 목록에 먼저 보이는 구성. 없으면 빈 문자열 */
  frontOptionName?: string
  frontOptionExtra?: number
  /** 검수 요청(REQUEST) 로그 횟수 */
  requestCount?: number
  blocks?: MallDetailBlock[]
  specs?: MallProductSpecInput[]
  options?: MallProductOptionInput[]
  reviews?: MallReview[]
  qnas?: MallQna[]
  logs?: MallProductLog[]
  viewCount: number
  soldCount: number
  rating: number
  reviewCount: number
  rejectReason: string
  requestedAt: string | null
  reviewedAt: string | null
  reviewer: string
  /** 공동구매 특별할인 이름. 상시몰·미지정은 빈 문자열 */
  saleName?: string
  saleStartAt: string | null
  createdAt: string
  updatedAt: string | null
  /** 관리자가 지정한 이달의 특가 여부 */
  isDeal?: boolean
  /** 테스트 전용 상품 — testemp/testpan/admin 만 노출 */
  isTest?: boolean
  /** 이달의 특가 정렬(작을수록 앞, 0=히어로) */
  dealSort?: number
  dealStartAt?: string | null
  dealEndAt?: string | null
  /** 관리자 내부 메모 */
  adminMemo?: string
  relatedCodes?: string[]
  substituteCodes?: string[]
}

export interface UploadedImage {
  url: string
  name: string
  size: number
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const SELLER_BASE = `${API_ORIGIN}/api/mall/seller/products`
const MALL_BASE = `${API_ORIGIN}/api/mall/products`

const fetchOptions: RequestInit = {
  credentials: 'include',
}

const jsonOptions: RequestInit = {
  ...fetchOptions,
  headers: { 'Content-Type': 'application/json' },
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드와 프론트 dev 서버를 확인해 주세요.')
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || !result.data) {
    throw new Error(result.message || fallback)
  }
  return result.data
}

/* ================================================================
   판매업체 — 내 상품
   ================================================================ */

export interface MallSale {
  code: string
  name: string
  start: string
  end: string
}

export async function fetchSellerProducts() {
  const response = await apiFetch(SELLER_BASE, fetchOptions)
  const result = await parseJson<{ products: ServerMallProduct[] }>(response)
  return unwrap(result, '상품 목록을 불러오지 못했습니다.').products
}

export async function fetchSellerSales() {
  const response = await apiFetch(`${API_ORIGIN}/api/mall/seller/sales`, fetchOptions)
  const result = await parseJson<{ sales: MallSale[] }>(response)
  return unwrap(result, '공동구매 특별할인을 불러오지 못했습니다.').sales
}

export async function fetchSellerProduct(code: string) {
  const response = await apiFetch(`${SELLER_BASE}/${encodeURIComponent(code)}`, fetchOptions)
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '상품을 불러오지 못했습니다.').product
}

export async function createSellerProduct(payload: MallProductPayload) {
  const response = await apiFetch(SELLER_BASE, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '상품 등록에 실패했습니다.').product
}

export async function updateSellerProduct(code: string, payload: MallProductPayload) {
  const response = await apiFetch(`${SELLER_BASE}/${encodeURIComponent(code)}`, {
    ...jsonOptions,
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '상품 저장에 실패했습니다.').product
}

export async function deleteSellerProduct(code: string) {
  const response = await apiFetch(`${SELLER_BASE}/${encodeURIComponent(code)}`, {
    ...fetchOptions,
    method: 'DELETE',
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '상품 삭제에 실패했습니다.')
  }
}

export async function setSellerProductStatus(code: string, status: MallProductStatus) {
  const response = await apiFetch(`${SELLER_BASE}/${encodeURIComponent(code)}/status`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '판매 상태를 바꾸지 못했습니다.')
  }
}

export async function setSellerProductStock(code: string, stock: number) {
  const response = await apiFetch(`${SELLER_BASE}/${encodeURIComponent(code)}/stock`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ stock }),
  })
  const result = await parseJson<{ stock: number; status: MallProductStatus }>(response)
  if (!result.success) {
    throw new Error(result.message || '재고를 저장하지 못했습니다.')
  }
  return result.data
}

/** 재고 추가(+N). 이력에 남는다. */
export async function addSellerProductStock(code: string, add: number) {
  const response = await apiFetch(`${SELLER_BASE}/${encodeURIComponent(code)}/stock`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ add }),
  })
  const result = await parseJson<{ stock: number; status: MallProductStatus }>(response)
  if (!result.success) {
    throw new Error(result.message || '재고를 추가하지 못했습니다.')
  }
  return unwrap(result, '재고를 추가하지 못했습니다.')
}

export interface ProductStockSaleRow {
  kind: 'sale'
  orderCode: string
  qty: number
  name: string
  option: string
  status: string
  amount: number
  buyerOrg: string
  at: string
}

export interface ProductStockAdjustRow {
  kind: 'adjust'
  action: string
  actor: string
  role: 'SELLER' | 'ADMIN' | string
  memo: string
  at: string
}

export interface ProductStockHistory {
  code: string
  name: string
  stock: number
  soldCount: number
  status: MallProductStatus
  storeName?: string
  sales: ProductStockSaleRow[]
  adjustments: ProductStockAdjustRow[]
}

export async function fetchSellerProductStockHistory(code: string) {
  const response = await apiFetch(`${SELLER_BASE}/${encodeURIComponent(code)}/stock-history`, {
    ...fetchOptions,
  })
  const result = await parseJson<ProductStockHistory>(response)
  return unwrap(result, '재고 이력을 불러오지 못했습니다.')
}

/** 이미지는 저장과 별개로 먼저 올려 두고, 돌려받은 URL을 상품에 붙인다. */
export async function uploadProductImages(files: File[]) {
  const body = new FormData()
  files.forEach((file) => body.append('images', file))

  const response = await apiFetch(`${SELLER_BASE}/images`, {
    ...fetchOptions,
    method: 'POST',
    body,
  })
  const result = await parseJson<{ images: UploadedImage[] }>(response)
  return unwrap(result, '이미지 업로드에 실패했습니다.').images
}

/* ================================================================
   몰 — 구매자 조회
   ================================================================ */

export async function fetchLiveProducts() {
  const response = await apiFetch(MALL_BASE, fetchOptions)
  const result = await parseJson<{ products: ServerMallProduct[] }>(response)
  return unwrap(result, '상품 목록을 불러오지 못했습니다.').products
}

export interface MallTopSalesProduct extends ServerMallProduct {
  rank: number
}

/** 전체 기간 구매확정 순매출 상위 상품. 매출액은 응답에 없다. */
export async function fetchTopSalesProducts() {
  const response = await apiFetch(`${MALL_BASE}/top-sales`, fetchOptions)
  const result = await parseJson<{ products: MallTopSalesProduct[] }>(response)
  return unwrap(result, '매출 상위 상품을 불러오지 못했습니다.').products
}

/**
 * 몰에 걸려 있는 상품 한 건.
 * 검수 재요청 · 숨김 · 반려로 내려간 상품은 404 가 오는데, 이때는 오류가 아니라
 * '지금은 없는 상품'이므로 null 을 돌려준다. 호출한 쪽이 캐시를 지울 수 있어야 한다.
 */
export async function fetchLiveProduct(code: string): Promise<ServerMallProduct | null> {
  const response = await apiFetch(`${MALL_BASE}/${encodeURIComponent(code)}`, fetchOptions)

  if (response.status === 404) {
    return null
  }

  const result = await parseJson<{ product: ServerMallProduct }>(response)
  return unwrap(result, '상품을 불러오지 못했습니다.').product
}

export interface MallPublicStore {
  sellerCode: string
  storeName: string
  shippingFee: number
  freeShipOver: number
  cutoff: string
  returnPolicy: string
  returnFee: number
}

export async function fetchMallStore(sellerCode: string) {
  const response = await apiFetch(
    `${API_ORIGIN}/api/mall/stores/${encodeURIComponent(sellerCode)}`,
    fetchOptions,
  )
  const result = await parseJson<{ store: MallPublicStore; products: ServerMallProduct[] }>(response)
  return unwrap(result, '스토어를 불러오지 못했습니다.')
}

export async function fetchRestockNotifyStatus(code: string) {
  const response = await apiFetch(
    `${MALL_BASE}/${encodeURIComponent(code)}/restock-notify`,
    fetchOptions,
  )
  const result = await parseJson<{ subscribed: boolean }>(response)
  return unwrap(result, '재입고 알림 상태를 확인하지 못했습니다.').subscribed
}

export async function subscribeRestockNotify(code: string) {
  const response = await apiFetch(`${MALL_BASE}/${encodeURIComponent(code)}/restock-notify`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<{ subscribed: boolean }>(response)
  if (!result.success) {
    throw new Error(result.message || '재입고 알림을 신청하지 못했습니다.')
  }
  return Boolean(result.data?.subscribed)
}

export async function unsubscribeRestockNotify(code: string) {
  const response = await apiFetch(`${MALL_BASE}/${encodeURIComponent(code)}/restock-notify`, {
    ...fetchOptions,
    method: 'DELETE',
  })
  const result = await parseJson<{ subscribed: boolean }>(response)
  if (!result.success) {
    throw new Error(result.message || '재입고 알림을 취소하지 못했습니다.')
  }
  return false
}

/* ================================================================
   변환 — 서버 상품을 몰 화면이 쓰는 모양으로
   ================================================================ */

export function toMallProduct(product: ServerMallProduct): MallProduct {
  return {
    id: product.code,
    name: product.name,
    category: product.category,
    summary: product.summary,
    description: product.description,
    image: product.thumbnail,
    images: product.images ?? [],
    listPrice: product.listPrice || product.price,
    price: product.price,
    unit: product.unit,
    badge: product.badge || undefined,
    tags: product.tags ?? [],
    highlights: (product.specs ?? []).map((spec) => ({ label: spec.label, value: spec.value })),
    rating: product.rating,
    reviewCount: product.reviewCount,
    soldCount: product.soldCount,
    freeShipping: false,
    minOrder: product.minOrder || 1,
    orderStep: normalizeOrderStep(product.orderStep),
    maxOrder: product.maxOrder || 0,
    mallKind: product.mallKind === 'REGULAR' ? 'REGULAR' : product.mallKind === 'SALE' ? 'SALE' : undefined,
    sellUnit: normalizeSellUnit(product.sellUnit),
    imageShows: normalizeImageShows(product.imageShows),
    packQty: normalizePackQty(product.packQty),
    shippingCharge: normalizeShippingCharge(product.shippingCharge),
    shippingEvery: normalizeShippingEvery(product.shippingEvery),
    reviews: product.reviews ?? [],
    qnas: product.qnas ?? [],

    sellerCode: product.sellerCode,
    storeName: product.storeName,
    storeCutoff: product.storeCutoff || '',
    storeReturnPolicy: product.storeReturnPolicy || '',
    storeReturnFee: Math.max(0, Number(product.storeReturnFee) || 0),
    returnEvery: Math.max(1, Number(product.returnEvery) || 10),
    returnFee: Math.max(0, Number(product.returnFee) || 0),
    blocks: product.blocks ?? [],
    options: (product.options ?? [])
      .filter((option) => option.use !== false)
      .map((option) => ({ ...option, front: option.front === true })),
    frontOptionName: product.frontOptionName || '',
    frontOptionExtra: Number(product.frontOptionExtra) || 0,
    stock: product.stock,
    soldOut: product.status === 'SOLDOUT' || product.stock <= 0,
    leadTime: product.leadTime,
    shippingFee: product.shippingFee,
    shippingCharge: normalizeShippingCharge(product.shippingCharge),
    shippingEvery: normalizeShippingEvery(product.shippingEvery),
    origin: product.origin,
    maker: product.maker,
    isDeal: Boolean(product.isDeal),
    isTest: Boolean(product.isTest),
    dealSort: Number(product.dealSort) || 0,
    dealStartAt: product.dealStartAt || null,
    dealEndAt: product.dealEndAt || null,
    relatedCodes: product.relatedCodes ?? [],
    substituteCodes: product.substituteCodes ?? [],
  }
}
